import React from "react"

import {
  getCompareByDisplayLabel,
  getCompareByGroupKeys,
  groupMetricOutputsByCompareBy,
  type CompareBy,
} from "@/metrics/compare-by"
import { MiniCompareBarChart } from "@/metrics/metric-display"
import { MetricRecord } from "@/metrics/runtime"
import { Input } from "@coval/ui"

import type { RunMetricOutput } from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"

import { AggregateOverviewSet } from "../../components/aggregate/AggregateOverviewSet"
import { SingleSet } from "../../components/single/SingleSet"

/**
 * Type base class for set output metrics.
 *
 * Handles metrics that produce arrays of string values (e.g. tool calls,
 * multi-select classifications).
 */
export class SetMetricRecord extends MetricRecord {
  // ─── Comparison ──────────────────────────────────────────────────────────

  compareValues(
    value1: { set_value?: string[] | null },
    value2: { set_value?: string[] | null },
  ): boolean {
    const a = value1.set_value
    const b = value2.set_value
    if (a == null || b == null) {
      return a === b
    }
    const sortedA = [...a].sort()
    const sortedB = [...b].sort()
    return (
      sortedA.length === sortedB.length &&
      sortedA.every((v, i) => v === sortedB[i])
    )
  }

  // ─── Badge ────────────────────────────────────────────────────────────────

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    if (mo.set_value) {
      return (
        <div>
          {mo.set_value.join(", ").slice(0, 10)}
          {mo.set_value.length > 10 ? "..." : ""}
        </div>
      )
    }
    return <div>N/A</div>
  }

  // ─── Single output ────────────────────────────────────────────────────────

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode | null {
    return <SingleSet mo={mo} />
  }

  // ─── Overview / run review ────────────────────────────────────────────────

  getComparedOverviewNode(
    runRows: RunRowLike[],
    compareBy: CompareBy,
  ): React.ReactNode | null {
    const allGroupKeys = getCompareByGroupKeys(runRows, compareBy)
    if (!runRows.length || allGroupKeys.length === 0) {
      return null
    }
    const mo = this._getMetricOutputsFromRunRows(runRows)
    const groups = groupMetricOutputsByCompareBy(mo, runRows, compareBy)
    const data = allGroupKeys.map((key) => {
      const groupMo = groups.get(key) ?? []
      const label = getCompareByDisplayLabel(key, compareBy)
      return {
        id: key,
        label: label.length > 16 ? `${label.slice(0, 15)}…` : label,
        value: groupMo.length,
        color: compareBy.colors[key],
      }
    })
    return (
      <MiniCompareBarChart
        key={`${compareBy.dimension}-${compareBy.metadataKey ?? ""}`}
        data={data}
        valueLabel="Count"
        formatValue={(v) => String(Math.round(v))}
      />
    )
  }

  getOverviewNode(mo: RunMetricOutput[]): React.ReactNode | null {
    if (!mo[0]) {
      return null
    }
    return <AggregateOverviewSet mos={mo} />
  }

  // ─── Alert forms ─────────────────────────────────────────────────────────

  renderAggregateAlertForm(
    threshold: string,
    onThresholdChange: (value: string) => void,
    matchValue?: string,
    onMatchValueChange?: (value: string) => void,
  ): React.ReactNode {
    return (
      <div className="flex gap-2 flex-1">
        <Input
          placeholder="Fraction (0-1)"
          value={threshold}
          onChange={(e) => onThresholdChange(e.target.value)}
          type="number"
          step="0.01"
          min="0"
          max="1"
          className="w-32"
        />
        <Input
          placeholder="Match value"
          value={matchValue || ""}
          onChange={(e) => (onMatchValueChange || (() => {}))(e.target.value)}
          className="flex-1"
        />
      </div>
    )
  }
}
